import React, { useState, useRef, useEffect } from "react";
import { X, LogOut, Plus } from "lucide-react";
import { useSelector } from "react-redux";
import useInstagramIntegrationHook from "../../CustomHooks/useInstagramIntegrationHook";
import user from "../../ImageAssets/user.jpg";
import { callNonStoreApi } from "../../NonStoreApis";
import { enqueueSnackbar } from "notistack";
import {
  InstagramIntegrationApi,
  UpdateInstagramProfile,
} from "../../Api/ApiEndPoints";
import {
  isArrayNullOrUndefined,
  isNullOrUndefined,
  isValidImage,
} from "../../utils";
import { Button } from "antd";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import axios from "../../axios";
import { useDispatch } from "react-redux";

const InstagramAccountSwitcher = ({ style, username, onClick, disabled }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const { credentials } = useSelector((state) => state.instagram);
  const { apiUserData } = useSelector((state) => state.user);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(
    credentials?.profile_picture_url || user
  );

  const { accounts, handleSetSelectedAccountCredentials } =
    useInstagramIntegrationHook(username);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  // Close popup when clicking outside
  const fetchProfileImage = async () => {
    try {
      const result = await isValidImage(credentials?.profile_picture_url);
      if (result) {
        setPhotoUrl(credentials?.profile_picture_url);
      } else {
        throw new Error("Media not loading");
      }
    } catch (error) {
      try {
        const response = await axios.get(
          `https://graph.instagram.com/v20.0/me?fields=user_id,name,username,profile_picture_url&access_token=${credentials?.access_token}`
        );

        const url = response.data.profile_picture_url || user;
        setPhotoUrl(url);
        const instagram_profile_response = await callNonStoreApi(
          UpdateInstagramProfile,
          {
            ...response.data,
          }
        );
        // api call to update media_url
        // dispatch(
        //   updateInstagramAccountDataAction(instagram_profile_response.data.data)
        // );
      } catch (error) {
        setPhotoUrl(user);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchProfileImage();
  }, [credentials]);

  if (
    isNullOrUndefined(credentials) ||
    isArrayNullOrUndefined(Object.values(credentials))
  ) {
    return (
      <>
        <Button
          type="primary"
          onClick={() => {
            setIsApiLoading(true);
            callNonStoreApi(InstagramIntegrationApi, null, {
              params: {
                username: apiUserData?.userName,
              },
            })
              .then((response) => {
                window.location.href = response.data.instagramAuthUrl;
              })
              .catch((error) => {
                enqueueSnackbar(
                  "There is some error in connecting to Instagram",
                  { variant: "error" }
                );
                setIsApiLoading(false);
              });
          }}
        >
          Link Instagram
        </Button>
      </>
    );
  }

  return (
    <div>
      <div
        className="flex flex-col items-start"
        style={{ position: "relative" }}
      >
        <div
          key={credentials.id}
          ref={buttonRef}
          className="flex items-center pl-2 pr-0 py-1 hover:bg-gray-50 cursor-pointer border-2 border-solid border-gray-200 rounded-md gap-3"
          onClick={() => {
            if (disabled) return;
            setIsOpen(!isOpen);
          }}
        >
          <img
            src={photoUrl}
            className={`w-7 h-7 rounded-full flex items-center justify-center  text-white`}
          ></img>
          <div className="flex-1">
            <div className="text-xs font-medium text-gray-900">
              {credentials.name || "Instagram User"}
            </div>
            <div className="text-xs text-gray-500">@{credentials.username}</div>
          </div>
          <ArrowDropDownOutlinedIcon />
        </div>

        {isOpen && (
          <div
            ref={popupRef}
            style={
              style
                ? style
                : {
                    position: "fixed",
                    zIndex: 11111,
                    right: "48px",
                    top: "10%",
                  }
            }
            className="mt-2 bg-white rounded-xl w-80 shadow-xl border border-gray-200"
          >
            {/* Account List */}
            <div className="py-2">
              {accounts.map((account) => (
                <InstaAccount
                  account={account}
                  handleSetSelectedAccountCredentials={
                    handleSetSelectedAccountCredentials
                  }
                  isOpen={isOpen}
                  onClick={onClick}
                  setIsOpen={setIsOpen}
                  key={account.id}
                />
              ))}

              {/* Add Account Button */}
              <div
                className="border-t border-gray-200 mt-2"
                onClick={() => {
                  setIsApiLoading(true);
                  callNonStoreApi(InstagramIntegrationApi, null, {
                    params: {
                      username: apiUserData?.userName,
                    },
                  })
                    .then((response) => {
                      window.location.href = response.data.instagramAuthUrl;
                    })
                    .catch((error) => {
                      enqueueSnackbar(
                        "There is some error in connecting to Instagram",
                        { variant: "error" }
                      );
                      setIsApiLoading(false);
                    });
                }}
              >
                <button className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50">
                  <Plus className="mr-3 h-5 w-5" />
                  <span>Add account</span>
                </button>

                {/* <button className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50">
                  <LogOut className="mr-3 h-5 w-5" />
                  <span>Log out</span>
                </button> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstagramAccountSwitcher;

const InstaAccount = ({
  account,
  handleSetSelectedAccountCredentials,
  setIsOpen,
  isOpen,
  onClick,
}) => {
  const dispatch = useDispatch();
  const [photoUrl, setPhotoUrl] = useState("");
  const fetchProfileImage = async () => {
    try {
      const result = await isValidImage(account?.profile_picture_url);
      if (result) {
        setPhotoUrl(account?.profile_picture_url);
      } else {
        throw new Error("Media not loading");
      }
    } catch (error) {
      try {
        const response = await axios.get(
          `https://graph.instagram.com/v20.0/me?fields=user_id,name,username,profile_picture_url&access_token=${account?.access_token}`
        );

        const url = response.data.profile_picture_url || user;
        setPhotoUrl(url);
        const instagram_profile_response = await callNonStoreApi(
          UpdateInstagramProfile,
          {
            ...response.data,
          }
        );
        // dispatch(
        //   updateInstagramAccountDataAction(instagram_profile_response.data.data)
        // );
        // api call to update media_url
      } catch (error) {
        setPhotoUrl(user);
      }
    }
  };

  useEffect(() => {
    fetchProfileImage();
  }, []);

  return (
    <div
      key={account.id}
      className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
      onClick={() => {
        if (onClick) {
          return onClick({
            username: account.username,
            name: account.name,
            instaId: account.id,
            instaUserId: account.user_id,
          });
        }
        handleSetSelectedAccountCredentials(account.username);
        setIsOpen(!isOpen);
      }}
    >
      <img
        src={photoUrl}
        className={`w-8 h-8 ${account.color} rounded-full flex items-center justify-center mr-3 text-white`}
      >
        {account.initial}
      </img>
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900">
          {account.name || "Instagram User"}
        </div>
        <div className="text-sm text-gray-500">@{account.username}</div>
      </div>
    </div>
  );
};
